import type { DocumentError } from 'apiConnector/document/document';
import { useAppSelector } from 'hooks/useAppSelector';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { documentDownloaded, documentDownloadFailed } from 'state/document/actions';
import type { AlertSeverity, Msg } from 'state/notifications/notification.model';
import { showNotificationThunk } from 'state/notifications/notification.thunk';
import { dashboardTileGroupChanged, dashboardTilePinToggled } from 'state/quotes/dashboard/dashboard.actions';
import { openQuoteTabThunk, quoteRepriceThunk } from 'state/quotes/dashboard/dashboard.thunks';
import type { PinnedGroup } from 'state/quotes/dashboard/selectors';
import { canTileBeRepriced, getRegulationData, getTileFormData } from 'state/quotes/dashboard/selectors';
import type { GroupColor } from 'state/quotes/dashboard/tiles/tiles.model';
import { dashboard } from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';

export interface TileOwnProps {
  quoteId: string;
  className?: string;
  customGroups: PinnedGroup[];
  onDashboardClosed(): void;
  onTileClosed(): void;
}

export function useMapStateToProps({ quoteId }: Pick<TileOwnProps, 'quoteId'>) {
  const metadata = useAppSelector(state => dashboard.getTileMetadata(state, quoteId));
  const hasAssociatedTab = useAppSelector(state => dashboard.hasAssociatedTabId(state, quoteId));
  const data = useAppSelector(state => dashboard.getTileData(state, quoteId));
  const canBeRepriced = useAppSelector(state => canTileBeRepriced(state, quoteId));
  const regulationData = useAppSelector(state => getRegulationData(state, data?.status, quoteId));
  const termsheetParams = useAppSelector(state => dashboard.getTermsheetParams(state, quoteId));
  const formData = useAppSelector(state => getTileFormData(state, quoteId));

  return {
    data,
    metadata,
    regulationData,
    hasAssociatedTab,
    canBeRepriced,
    termsheetParams,
    formData,
  };
}

type StateProps = ReturnType<typeof useMapStateToProps>;

export function useDispatchToProps(
  { quoteId, onDashboardClosed }: Pick<TileOwnProps, 'quoteId' | 'onDashboardClosed'>,
  { data, metadata }: Pick<StateProps, 'data' | 'metadata'>,
) {
  const dispatch = useDispatch<ThunkDispatch>();

  const externalQuoteId = data?.externalQuoteId;
  const workspace = data?.workspace;
  const status = data?.status;

  const onWorkspaceTabOpened = useCallback(() => {
    logAnalytics.trackEvent('Active quotes dashboard', 'Open tab through dashboard');
    dispatch(openQuoteTabThunk(quoteId, externalQuoteId!, workspace!));
    onDashboardClosed();
  }, [onDashboardClosed, dispatch, quoteId, externalQuoteId, workspace]);

  const onReprice = useCallback(() => {
    logAnalytics.trackEvent('Active quotes dashboard', 'Reprice tile though dashboard');
    dispatch(quoteRepriceThunk(quoteId, status));
  }, [status, dispatch, quoteId]);

  const onPinToggled = useCallback(() => {
    const wasPreviouslyPinned = metadata.isPinned;

    logAnalytics.trackEvent('Active quotes dashboard', 'Pin or unpin a dashboard tile');
    dispatch(dashboardTilePinToggled(quoteId));

    dispatch(
      showNotificationThunk({
        level: 'success',
        title: {
          id: wasPreviouslyPinned
            ? 'dashboard.notification.tileUnPinned.title'
            : 'dashboard.notification.tilePinned.title',
        },
      }),
    );
  }, [dispatch, metadata.isPinned, quoteId]);

  const onGroupChanged = useCallback(
    (group: string, color?: GroupColor) => {
      logAnalytics.trackEvent('Active quotes dashboard', 'Group a tile in a custom group');
      dispatch(dashboardTileGroupChanged(quoteId, group, color));

      dispatch(
        showNotificationThunk({
          level: 'success',
          title: {
            id: 'dashboard.notification.tilePinned.title',
          },
        }),
      );
    },
    [dispatch, quoteId],
  );

  const onUnGrouped = useCallback(() => {
    dispatch(dashboardTileGroupChanged(quoteId));
    dispatch(
      showNotificationThunk({
        level: 'success',
        title: {
          id: 'dashboard.notification.tileUnPinned.title',
        },
      }),
    );
  }, [dispatch, quoteId]);

  const onTermsheetSuccess = useCallback(
    (tabId: string, locale: string) => dispatch(documentDownloaded(tabId, locale, 'Termsheet', 'PDF')),
    [dispatch],
  );

  const onTermsheetError = useCallback(
    (tabId: string, locale: string, error: DocumentError) =>
      dispatch(documentDownloadFailed(tabId, locale, error, 'Termsheet', 'PDF')),
    [dispatch],
  );

  const onNotificationShown = useCallback(
    (level: AlertSeverity, title: Msg, content: Msg | undefined) =>
      dispatch(showNotificationThunk({ level, title, content: content ? [content] : undefined })),
    [dispatch],
  );

  return {
    onWorkspaceTabOpened,
    onReprice,
    onPinToggled,
    onGroupChanged,
    onUnGrouped,
    onTermsheetSuccess,
    onTermsheetError,
    onNotificationShown,
  };
}
